import { Link } from 'react-router-dom';
import { setConsent } from '../core/analytics/consent-store';
import { measurementId } from '../core/analytics/google-analytics';
import { useCookieConsent } from '../core/hooks/use-cookie-consent';
import { useHydrated } from '../core/hooks/use-hydrated';
import { useLanguage } from '../core/hooks/use-language';
import { useTranslation } from '../core/hooks/use-translation';

/**
 * Asks before anything optional is loaded.
 *
 * **It only appears where there is something to ask about.** A build with no measurement ID sets no
 * optional cookie at all, so a banner on it would be asking permission for nothing — which teaches
 * people to dismiss the question without reading it, and is the reason so many of these are noise.
 * Development and preview builds are therefore quiet.
 *
 * **The two answers are equally easy.** One button is not a link, the other is not greyed, and
 * neither is hidden behind a second screen. A choice where refusing costs more clicks than agreeing
 * is not a choice, and this site's own privacy policy is one scroll away from saying so.
 *
 * **It does not block the page.** The session cookie the portal needs is not optional and is not
 * what is being asked about, so there is nothing here that has to be answered before the site can
 * be read. That is also why it is a `region` and not a modal `dialog`: a modal would claim the page
 * is unusable until dismissed, which would be a lie the keyboard would then have to live with.
 *
 * **It is not in the prerendered bytes.** The content pages are written at build time, where no
 * decision has been made yet, so a banner rendered there would appear for a tenth of a second on
 * every page load and then remove itself for the visitor who already answered — which is how a
 * consent question becomes something people dismiss without reading. It waits for hydration and
 * then asks once, of the people who have not answered.
 *
 * It renders in `LanguageLayout`, above every page and below none, because the landing page, the
 * booking wizard and the legal page each carry their own chrome and share no header to hang it off.
 */
export function CookieBanner() {
    const { t } = useTranslation();
    const language = useLanguage();
    const consent = useCookieConsent();
    const hydrated = useHydrated();

    if (!hydrated || consent !== null || measurementId() === null) {
        return null;
    }

    return (
        <div
            role="region"
            aria-label={t('cookies.title')}
            className="fixed inset-x-0 bottom-0 z-[2000] p-3 sm:p-5"
        >
            <div className="mx-auto flex max-w-3xl flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_8px_28px_rgba(0,0,0,0.18)] sm:flex-row sm:items-center sm:gap-6 sm:p-6">
                <div className="flex-1">
                    <p className="text-base font-bold text-slate-900">{t('cookies.title')}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                        {t('cookies.body')}{' '}
                        <Link
                            to={`/${language}/policy`}
                            className="font-semibold text-brand-600 underline hover:text-brand-700"
                        >
                            {t('cookies.policy_link')}
                        </Link>
                    </p>
                </div>

                <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                    {/* The refusal comes first in the DOM, so it is the first control reached by
                        keyboard rather than the one after the agreement. */}
                    <button
                        type="button"
                        onClick={() => setConsent('denied')}
                        className="rounded-full border-2 border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
                    >
                        {t('cookies.decline')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setConsent('granted')}
                        className="rounded-full bg-brand-500 px-5 py-2.5 text-sm font-bold text-white shadow-[0_6px_20px_rgba(1,161,153,0.3)] transition hover:bg-brand-600"
                    >
                        {t('cookies.accept')}
                    </button>
                </div>
            </div>
        </div>
    );
}
